import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle } from '@mui/material';

import type { TravelerDocumentMatchDto } from '@bandido/shared/dtos';

import { useDebouncedState } from '@frontend/features/common/hooks/useDebouncedState';

import { useTravelerDocumentMatches } from '../queries';
import {
  isLookupDocumentNumber,
  otherTravelerMatches,
  sameDocumentNumber,
} from '../travelerDocumentLookup';
import { TravelerDocumentMatchList } from './TravelerDocumentMatchList';

const LOOKUP_DELAY_MS = 400;

interface Props {
  caseId: number;
  documentNumber: string;
  /** Fichas que ya se están usando o editando: no se ofrecen. */
  excludedTravelerIds?: readonly string[];
  actionLabel: string;
  disabled?: boolean;
  onUse: (match: TravelerDocumentMatchDto) => void;
}

/**
 * Detector de documentos: donde se escribe un número, avisa si ya hay una
 * ficha de la agencia con ese documento y ofrece usarla. No bloquea nada.
 */
export const DocumentMatchNotice = ({
  caseId,
  documentNumber,
  excludedTravelerIds = [],
  actionLabel,
  disabled = false,
  onUse,
}: Props) => {
  const { t } = useTranslation();
  const [lookupNumber, setLookupNumber] = useDebouncedState(
    documentNumber,
    LOOKUP_DELAY_MS,
  );
  useEffect(() => {
    setLookupNumber(documentNumber);
  }, [documentNumber, setLookupNumber]);

  // En un campo de documento vale la regla del backend (5 a 80 letras o
  // dígitos). Mientras se sigue escribiendo no se ofrece la ficha del número
  // anterior.
  const settled =
    isLookupDocumentNumber(documentNumber) &&
    sameDocumentNumber(lookupNumber, documentNumber);
  const lookup = useTravelerDocumentMatches(caseId, lookupNumber, settled);
  const matches = otherTravelerMatches(lookup.data, excludedTravelerIds);

  if (!settled) return null;
  // Un error no puede pasar por "nadie tiene este documento".
  if (lookup.isError) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        {t('travelers.documentMatches.lookupError')}
      </Alert>
    );
  }
  if (!matches.length) return null;

  return (
    <Alert severity="warning" sx={{ mt: 1 }}>
      <AlertTitle>{t('travelers.documentMatches.noticeTitle')}</AlertTitle>
      <TravelerDocumentMatchList
        matches={matches}
        actionLabel={actionLabel}
        busy={disabled}
        onUse={onUse}
      />
    </Alert>
  );
};
